/**
 * Settings Service
 * API calls for settings-related operations
 */

import { analytics } from "@/lib/analytics"
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from "@/lib/analytics/constants"
import { apiClient } from "@/lib/api-client"
import type {
  CalendarEvent,
  ConnectedOAuthAccount,
  ExternalCalendar,
  ExternalContact,
  PaginationInfo,
} from "../types/settings.types"

export interface NotificationSettingResponse {
  id: string
  userId: string
  type: string
  sendPushNotification: boolean
  sendEmailNotification: boolean
}

export interface NotificationSettingRequest {
  type: string
  sendPushNotification: boolean
  sendEmailNotification: boolean
}

/**
 * Change user password
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  const response = await apiClient.put<{
    success: boolean
    data: null
    message: string
  }>("/api/users/password", { oldPassword, newPassword })

  // Track password change
  analytics.track(ANALYTICS_EVENTS.PASSWORD_CHANGED)

  return { message: response.data.message }
}

/**
 * Set password for user without old password (OAuth users)
 */
export async function setPassword(newPassword: string): Promise<{ message: string }> {
  const response = await apiClient.put<{
    success: boolean
    data: null
    message: string
  }>("/api/users/password/set", { newPassword })

  // Track password set
  analytics.track(ANALYTICS_EVENTS.PASSWORD_CHANGED)

  return { message: response.data.message }
}

/**
 * Notification settings types
 */
export async function getNotificationSettings(): Promise<{
  settings: NotificationSettingResponse[]
}> {
  const response = await apiClient.get<{
    success: boolean
    data: { settings: NotificationSettingResponse[] }
    message: string
  }>("/api/users/notification-settings")
  return { settings: response.data.data.settings }
}

/**
 * Update notification settings
 */
export async function updateNotificationSettings(
  settings: NotificationSettingRequest[],
): Promise<{ settings: NotificationSettingResponse[] }> {
  const response = await apiClient.put<{
    success: boolean
    data: { settings: NotificationSettingResponse[] }
    message: string
  }>("/api/users/notification-settings", { settings })

  // Track notification settings changes
  const notificationTypes = settings.map(s => s.type).join(", ")
  analytics.track(ANALYTICS_EVENTS.NOTIFICATION_SETTINGS_CHANGED, {
    [ANALYTICS_PROPERTIES.NOTIFICATION_TYPES]: notificationTypes,
  })

  return { settings: response.data.data.settings }
}

/**
 * Delete user account
 */
export async function deleteAccount(password: string): Promise<{ message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: null
    message: string
  }>("/api/users/account", { data: { password } })

  // Track account deletion and reset analytics
  analytics.track(ANALYTICS_EVENTS.ACCOUNT_DELETED)
  analytics.reset()

  return { message: response.data.message }
}

/**
 * Send phone verification OTP
 */
export async function sendPhoneVerificationOtp(
  phoneNumber: string,
  countryCode: string = "+1",
): Promise<{ message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: null
    message: string
  }>("/api/users/phone/send-otp", { phoneNumber, countryCode })
  return { message: response.data.message }
}

/**
 * Verify phone OTP
 */
export async function verifyPhoneOtp(
  otp: string,
  phoneNumber: string,
  countryCode: string = "+1",
): Promise<{ message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { user: unknown }
    message: string
  }>("/api/users/phone/verify-otp", { otp, phoneNumber, countryCode })

  // Track phone added and update Mixpanel profile
  analytics.setUserProfile({
    $phone: phoneNumber,
    phone_verified: true,
  })

  analytics.track(ANALYTICS_EVENTS.PHONE_NUMBER_ADDED, {
    [ANALYTICS_PROPERTIES.PHONE]: phoneNumber,
  })

  return { message: response.data.message }
}

/**
 * Send email verification OTP for email change
 */
export async function sendEmailVerificationOtp(email: string): Promise<{ message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: null
    message: string
  }>("/api/users/email/send-otp", { email })
  return { message: response.data.message }
}

/**
 * Verify email OTP and update email
 */
export async function verifyEmailOtp(otp: string, email: string): Promise<{ message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { user: unknown }
    message: string
  }>("/api/users/email/verify-otp", { otp, email })

  // Track email change and update Mixpanel profile
  analytics.setUserProfile({
    $email: email,
    email_verified: true,
  })

  analytics.track(ANALYTICS_EVENTS.EMAIL_CHANGED, { [ANALYTICS_PROPERTIES.NEW_EMAIL]: email })

  return { message: response.data.message }
}

// ============================================================================
// OAUTH/EXTERNAL DATA SERVICES
// ============================================================================

/**
 * Get OAuth authorization URL
 */
export async function getOAuthUrl(
  provider: string,
  redirectUrl?: string,
): Promise<{ authUrl: string }> {
  const params = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : ""
  const response = await apiClient.get<{
    success: boolean
    data: { authUrl: string }
    message: string
  }>(`/api/oauth/${provider}${params}`)
  return { authUrl: response.data.data.authUrl }
}

/**
 * Get OAuth connection status
 */
export async function getOAuthConnectionStatus(
  provider: string,
): Promise<{ connected: boolean; account?: ConnectedOAuthAccount }> {
  const response = await apiClient.get<{
    success: boolean
    data: { connected: boolean; account?: ConnectedOAuthAccount }
    message: string
  }>(`/api/oauth/${provider}/status`)
  return response.data.data
}

/**
 * Get all connected OAuth accounts
 */
export async function getConnectedOAuthAccounts(): Promise<{ accounts: ConnectedOAuthAccount[] }> {
  const response = await apiClient.get<{
    success: boolean
    data: ConnectedOAuthAccount[]
    message: string
  }>("/api/users/oauth/accounts")
  return { accounts: response.data.data }
}

/**
 * Disconnect OAuth account
 */
export async function disconnectOAuthAccount(provider: string): Promise<{ message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: null
    message: string
  }>(`/api/oauth/${provider}`)
  return { message: response.data.message }
}

/**
 * Trigger manual sync
 */
export async function triggerOAuthSync(provider: string): Promise<{ message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: null
    message: string
  }>(`/api/oauth/${provider}/sync`)
  return { message: response.data.message }
}

/**
 * Get calendars for connected account
 */
export async function getCalendars(provider: string): Promise<{ calendars: ExternalCalendar[] }> {
  const response = await apiClient.get<{
    success: boolean
    data: ExternalCalendar[]
    message: string
  }>(`/api/oauth/${provider}/calendars`)
  return { calendars: response.data.data }
}

/**
 * Get calendar events
 */
export async function getCalendarEvents(
  provider: string,
  calendarId: string,
  startDate?: string,
  endDate?: string,
  page: number = 1,
  limit: number = 50,
): Promise<{ events: CalendarEvent[]; pagination: PaginationInfo }> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (startDate) params.set("startDate", startDate)
  if (endDate) params.set("endDate", endDate)
  params.set("calendarId", calendarId)

  const response = await apiClient.get<{
    success: boolean
    data: { events: CalendarEvent[]; pagination: PaginationInfo }
    message: string
  }>(`/api/oauth/${provider}/events?${params.toString()}`)
  return response.data.data
}

/**
 * Get contacts
 */
export async function getContacts(
  provider: string,
  filter: "all" | "on_platform" | "off_platform" = "all",
  page: number = 1,
  limit: number = 50,
  search?: string,
  city?: string,
  state?: string,
): Promise<{ contacts: ExternalContact[]; pagination: PaginationInfo }> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    filter,
  })

  if (search) params.set("search", search)
  if (city) params.set("city", city)
  if (state) params.set("state", state)

  const response = await apiClient.get<{
    success: boolean
    data: { contacts: ExternalContact[]; pagination: PaginationInfo }
    message: string
  }>(`/api/oauth/${provider}/contacts?${params.toString()}`)
  return response.data.data
}

// ============================================================================
// SUBSCRIPTION API
// ============================================================================

export interface SubscriptionDetails {
  id: string
  status: string
  planId: string | null
  planCode: string | null
  planName: string | null
  planPrice: number | null
  currency: string
  billingInterval: string | null
  billingIntervalCount: number | null
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  trialEnd: string | null
  isPremium: boolean
}

export interface ActiveSubscriptionPlanDetails {
  id: string
  code: string
  name: string
  description: string | null
  priceInCents: number
  currency: string
  interval: string
  intervalCount: number
  stripePriceId: string | null
}

export interface SubscriptionResponse {
  hasSubscription: boolean
  subscription: SubscriptionDetails | null
}

/**
 * Get user's subscription status
 */
export async function getSubscription(): Promise<SubscriptionResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: SubscriptionResponse
    message: string
  }>("/api/subscriptions")

  return response.data.data
}

export async function getActiveSubscriptionPlan(): Promise<ActiveSubscriptionPlanDetails> {
  const response = await apiClient.get<{
    success: boolean
    data: ActiveSubscriptionPlanDetails
    message: string
  }>("/api/subscriptions/plan")

  return response.data.data
}

/**
 * Create subscription checkout session
 */
export async function createSubscriptionCheckout(
  successUrl: string,
  cancelUrl: string,
): Promise<{ checkoutUrl: string; subscriptionId: string | null }> {
  const response = await apiClient.post<{
    success: boolean
    data: { checkoutUrl: string; subscriptionId: string | null }
    message: string
  }>("/api/subscriptions/checkout", {
    successUrl,
    cancelUrl,
  })

  return response.data.data
}

/**
 * Cancel subscription
 */
export async function cancelSubscription(): Promise<void> {
  await apiClient.post<{
    success: boolean
    data: null
    message: string
  }>("/api/subscriptions/cancel")
}

/**
 * Create payment update portal session
 */
export async function createPaymentUpdateSession(returnUrl: string): Promise<{ url: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { url: string }
    message: string
  }>("/api/subscriptions/portal", {
    returnUrl,
  })

  return response.data.data
}
